/**
 * Reading the site's words.
 *
 * Two things live here. The hook a component calls to get the dictionary for the
 * language it is currently being rendered in, and the small amount of machinery that
 * keeps the document itself in step with that language: `<html lang>`, the title, the
 * description, and the `hreflang` set.
 *
 * ## Where the dictionary comes from
 *
 * From the URL, through the router, because the URL is the only place the language is
 * written down. There is no preference to read and no stored state to fall out of
 * step with the page. A component asks for words and gets the words for the address
 * it is currently sitting at.
 *
 * ## Why `activeDictionary` exists
 *
 * `lib/api.ts` builds the sentences a failed request reports. It is a plain module
 * with no hook to call and no props to receive a dictionary through, and threading
 * one through every fetch would put a language argument on all of them for the sake
 * of one error message.
 *
 * So it reads the active dictionary from here, and this module holds the current one.
 * That is a service locator, and it is normally the wrong answer.
 *
 * Two things about when it is written, because both are easy to get backwards. The
 * write is in an effect, after commit, rather than during render. Writing during
 * render would be the genuinely dangerous choice: under concurrent rendering an
 * abandoned render leaves the locator pointing at a language that never reached the
 * screen. `StrictMode`'s double-invocation costs nothing, because that effect has no
 * cleanup and simply writes the same value twice.
 *
 * The consequence of writing after commit is a window on the very first paint where
 * the locator still holds the previous value. Nothing may therefore resolve a
 * sentence *through* this during render: `ApiError` carries a key and resolves its
 * sentence when it is read, and the one module that did resolve during render now
 * carries a code instead.
 */

import React, { useEffect, useMemo } from 'react';

import { en } from './en';
import { es } from './es';
import { ptBR } from './pt-BR';
import { htmlLang, intlLocale, LOCALES, localeLabel, localeShortLabel, type Locale } from './locale';
import type { Dictionary } from './types';
import { activeDictionary, setActiveDictionary } from './active.ts';
import { format, type Values } from './format.ts';
import { hrefFor, useRouter, type Route } from '../router';

export const DICTIONARIES: Readonly<Record<Locale, Dictionary>> = {
  'pt-BR': ptBR,
  en,
  es,
};

export type { Locale, Dictionary };
export { LOCALES, localeLabel, localeShortLabel, htmlLang, intlLocale };

/*
  Re-exported rather than re-implemented, so there is exactly one store. The store
  itself lives in `./active.ts` because two plain modules have to read it and the test
  runner imports those without a bundler in front of them.
*/
export { activeDictionary, setActiveDictionary };

/*
  Re-exported from a leaf module for the same reason the store is: this is a pure
  string function that a test imports directly, and a `.tsx` file cannot be loaded by
  `node --test` without a bundler.
*/
export { format, type Values };

export interface I18n {
  readonly locale: Locale;
  /** Every string on the page, for this language. */
  readonly t: Dictionary;
}

/**
 * The strings for the language this page is being rendered in.
 *
 * Throws outside the router rather than defaulting, for the same reason `useAuth`
 * does: a component that lost its provider is a bug in this codebase, and a default
 * here would quietly serve one language on a page written in another.
 */
export function useI18n(): I18n {
  const { locale } = useRouter();
  return useMemo(() => ({ locale, t: DICTIONARIES[locale] }), [locale]);
}

/**
 * Renders a template that contains both `{name}` values and `*emphasis*`.
 *
 * Split on the asterisks and emitted as real elements. The alternative,
 * `dangerouslySetInnerHTML`, would mean handing a template to an HTML parser, and a
 * template is exactly the kind of string that grows a user-supplied value into it one
 * refactor later. There is no HTML parser here and therefore no injection surface.
 *
 * An unmatched `*` is treated as literal text rather than swallowing the rest of the
 * sentence, so a typo shows up as a stray character rather than as missing copy.
 */
export const RichText: React.FC<{ text: string; values?: Values }> = ({ text, values }) => {
  /*
    Split the template, then fill each half. The other order is the bug this shape
    avoids: splitting the *filled* string lets an asterisk inside a substituted value
    re-pair every segment after it, so an address typed as `a*b@x.com` into the
    confirm-your-email form rendered the instruction after it in bold and lost the
    sentence boundary with it. The value is form state and the form is `noValidate`,
    so nothing upstream checks what is in it.
  */
  const segments = text.split('*');

  if (segments.length === 1) return <>{format(text, values)}</>;

  return (
    <>
      {segments.map((segment, index) => {
        const part = format(segment, values);
        // An unpaired trailing `*` lands on an even index and stays literal text,
        // which is what makes a typo in a dictionary visible instead of silent.
        return index % 2 === 1 ? (
          <strong key={index} className="font-semibold text-cream">
            {part}
          </strong>
        ) : (
          part
        );
      })}
    </>
  );
};

const ORIGIN = 'https://orobas.app';

/**
 * Addresses that are really visited but never searched for.
 *
 * `/auth/callback` is where the desktop apps send a browser carrying an OAuth code.
 * Publishing a canonical and three `hreflang` alternates for it would be asking a
 * crawler to index an authorisation callback, which is at best noise and at worst a
 * set of near-identical URLs differing only by a language prefix.
 */
const UNINDEXED: ReadonlySet<Route> = new Set<Route>(['auth-callback']);

/**
 * Everything about the document that depends on the language.
 *
 * Rendered once, near the root, and does four things a browser cannot do for a
 * single-page app on its own:
 *
 *  - `<html lang>`. Without it a screen reader reads English words with Portuguese
 *    phonetics, which is the difference between a page that works and a page that
 *    merely loads.
 *  - The title and the two descriptions. `index.html` ships the Portuguese ones
 *    because that is what an unprefixed address means; this replaces them the moment
 *    the address says otherwise.
 *  - `hreflang` for the three languages, plus a canonical pointing at this page's own
 *    address. Written by hand rather than generated per route because this is a
 *    client-rendered app: the server sends one `index.html` for every path, so the
 *    only place that can state which URLs are the same page in which language is
 *    here.
 *
 * The links are replaced rather than appended to, so a language change does not
 * leave the previous language's alternates behind telling a crawler the page is in
 * two languages at once.
 */
export const LocaleHead: React.FC = () => {
  const { locale, route } = useRouter();
  const dictionary = DICTIONARIES[locale];

  useEffect(() => {
    setActiveDictionary(dictionary);
    document.documentElement.lang = htmlLang(locale);
  }, [dictionary, locale]);

  useEffect(() => {
    const set = (selector: string, attribute: string, value: string) => {
      const element = document.head.querySelector<HTMLMetaElement>(selector);
      element?.setAttribute(attribute, value);
    };

    document.title = dictionary.meta.title;
    set('meta[name="description"]', 'content', dictionary.meta.description);
    set('meta[property="og:title"]', 'content', dictionary.meta.ogTitle);
    set('meta[property="og:description"]', 'content', dictionary.meta.ogDescription);
    set('meta[property="og:locale"]', 'content', locale.replace('-', '_'));
  }, [dictionary, locale]);

  useEffect(() => {
    /*
      The static set goes first, before the unindexed check. `index.html` ships the
      home page's canonical and alternates, and returning early above this line left
      them in place on a callback: the address that was supposed to publish nothing
      was publishing the home page's, which is worse than publishing nothing.
    */
    const stale = document.head.querySelectorAll<HTMLLinkElement>(
      'link[rel="canonical"], link[rel="alternate"]',
    );
    for (const link of stale) link.remove();

    // A callback carries a one-time code in its query. There is nothing to describe
    // and nothing to point a crawler at.
    if (UNINDEXED.has(route)) return;

    // The alternates describe pages, not orders. An `?order=` address is the same
    // page as the bare one, and pointing a canonical at it would ask a crawler to
    // treat somebody's receipt as the canonical account page.
    const alternates: HTMLLinkElement[] = [];

    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `${ORIGIN}${hrefFor(route, locale)}`;
    alternates.push(canonical);

    for (const candidate of LOCALES) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = htmlLang(candidate);
      link.href = `${ORIGIN}${hrefFor(route, candidate)}`;
      alternates.push(link);
    }

    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.hreflang = 'x-default';
    xDefault.href = `${ORIGIN}${hrefFor(route, 'pt-BR')}`;
    alternates.push(xDefault);

    /*
      Appending rather than replacing would leave both sets, and an alternates list
      naming eight URLs for three languages tells a crawler the canonical page is
      whichever it happened to read first.
    */
    for (const link of alternates) document.head.appendChild(link);
    return () => {
      for (const link of alternates) link.remove();
    };
  }, [locale, route]);

  return null;
};

/**
 * Re-exported so components do not have to know that the route type lives in the
 * router. A switcher, a link and a head tag all need the same notion of "where".
 */
export type { Route };